import React from 'react';
import { Archive } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { ArtifactIcon } from '../artifact-snippet';
import { artifactTypeLabel } from './artifact-type-label';

/**
 * What the artifact panel shows in place of an archived resource.
 *
 * Archiving does not delete anything, so the panel used to mount the resource's
 * page as normal and the page had no idea it was looking at something that had
 * been put away. What arrived on screen was the page's loading skeleton, with
 * nothing behind it that would ever resolve — a Canvas of grey blocks and no
 * sentence anywhere saying why. This is that sentence.
 *
 * A notice rather than the page plus a band: `ArchivedNotice` is the right
 * shape on a full editor, where the archived thing is still the point of the
 * page and you may well have come to read it. In the panel the artifact is one
 * tab beside a conversation, and it is already gone from the switcher and the
 * index — a live editor for it here would be the one surface still treating it
 * as current.
 *
 * Deliberately offers no way back: nothing in the product unarchives a
 * resource, so a "restore" affordance here would be an invitation to a place
 * that does not exist. It says what happened and stops.
 */
export const ArchivedArtifactNotice: React.FC<{
  artifactType: string;
  title?: string;
}> = ({ artifactType, title }) => {
  const label = artifactTypeLabel(artifactType);
  const trimmedTitle = title?.trim();
  const heading =
    trimmedTitle !== undefined && trimmedTitle !== ''
      ? `"${trimmedTitle}" is archived`
      : `This ${label} is archived`;

  return (
    <div className="h-full w-full flex items-center justify-center bg-background p-6">
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        {/* Same badge construction as `UnsharedArtifactNotice`: the artifact's
            own icon, marked in the corner with what happened to it. The two
            states are the same kind of answer — "not this, and here is why" —
            and reading as one family is what keeps either from looking like a
            fault. */}
        <span className="relative flex size-12 shrink-0 items-center justify-center rounded-full border border-input bg-muted/50 text-muted-foreground">
          <ArtifactIcon artifactType={artifactType} className="size-5" />
          <span className="absolute -bottom-0.5 -right-0.5 flex size-5 items-center justify-center rounded-full border border-background bg-muted text-muted-foreground">
            <Archive className="size-3" />
          </span>
        </span>
        <Text.P className="text-sm font-medium text-foreground">
          {heading}
        </Text.P>
        <Text.P className="text-xs text-muted-foreground">
          It no longer appears in this chat's artifacts or in the Library. The{' '}
          {label} itself is not deleted — anything already built on it keeps
          working.
        </Text.P>
      </div>
    </div>
  );
};
